const COUNTRY_TOKEN_ALIASES = {
  US: ["US", "USA", "U.S.A.", "United States", "United States of America"],
  UK: ["UK", "United Kingdom", "England", "Great Britain"],
  USSR: ["USSR", "Soviet Union"],
  RU: ["RU", "Russia", "Russian Federation"],
  EU: ["EU", "Europe", "European Union", "EEC"],
  WW: ["WW", "Worldwide", "World", "[Worldwide]"],
}

const DICTIONARY_FALLBACK_TITLES = {
  US: ["USA"],
  UK: ["England"],
  USSR: ["Russia"],
  EU: ["EEC"],
  WW: ["EEC"],
}

function canonicalizeCountryDictionaryRow(countryItem) {
  if (!countryItem || typeof countryItem !== "object") return null

  const id = firstNonBlankValue([countryItem.id, countryItem.m_item_id])
  const title = firstNonBlankValue([
    countryItem.title,
    countryItem.m_item_title,
    countryItem.short_name,
    countryItem.m_item_short_name,
  ])

  return {
    ...countryItem,
    id,
    title,
  }
}

function safeTrim(value) {
  return typeof value === "string" ? value.trim() : ""
}

function normalizeCandidate(value) {
  return safeTrim(value).toLowerCase()
}

function firstNonBlankValue(candidates = []) {
  return (
    candidates.find((candidate) => {
      if (candidate == null) return false
      if (typeof candidate === "string") return safeTrim(candidate) !== ""
      return true
    }) ?? ""
  )
}

function extractCandidateStrings(countryItem) {
  if (!countryItem || typeof countryItem !== "object") return []

  return [
    countryItem.title,
    countryItem.m_item_title,
    countryItem.short_name,
    countryItem.m_item_short_name,
    countryItem.discogs_name,
    countryItem.m_item_discogs_name,
  ].filter(
    (value, index, list) =>
      safeTrim(value) !== "" && list.indexOf(value) === index
  )
}

export function canonicalizeCountryToken(rawValue) {
  const trimmed = safeTrim(rawValue)
  if (trimmed === "") return ""

  for (const [token, aliases] of Object.entries(COUNTRY_TOKEN_ALIASES)) {
    if (
      aliases.some(
        (alias) => normalizeCandidate(alias) === normalizeCandidate(trimmed)
      )
    ) {
      return token
    }
  }

  if (/^[A-Z]{2,4}$/.test(trimmed)) {
    return trimmed
  }

  return trimmed
}

export function normalizeCountryToken(value) {
  if (value == null) return ""

  if (typeof value === "string") {
    return canonicalizeCountryToken(value)
  }

  if (Array.isArray(value)) {
    return value
      .map((entry) => normalizeCountryToken(entry))
      .filter(Boolean)
      .join("/")
  }

  if (typeof value === "object") {
    return canonicalizeCountryToken(
      firstNonBlankValue([
        value.short_name,
        value.m_item_short_name,
        value.title,
        value.m_item_title,
      ])
    )
  }

  return ""
}

export function resolveCountryDictionaryItem(countryString, countryDict = []) {
  if (typeof countryString !== "string" || safeTrim(countryString) === "") {
    return null
  }

  if (!Array.isArray(countryDict) || countryDict.length === 0) {
    return null
  }

  const normalizedInput = normalizeCandidate(countryString)
  const canonicalToken = canonicalizeCountryToken(countryString)
  const normalizedCanonicalToken = normalizeCandidate(canonicalToken)
  const fallbackTitles = DICTIONARY_FALLBACK_TITLES[canonicalToken] || []
  const normalizedFallbackTitles = fallbackTitles.map((value) =>
    normalizeCandidate(value)
  )

  const matchedItem = countryDict.find((item) => {
    const normalizedCandidates = extractCandidateStrings(item).map(
      (candidate) => normalizeCandidate(candidate)
    )

    if (normalizedCandidates.includes(normalizedInput)) {
      return true
    }

    if (
      normalizedCanonicalToken &&
      normalizedCandidates.includes(normalizedCanonicalToken)
    ) {
      return true
    }

    return normalizedFallbackTitles.some((candidate) =>
      normalizedCandidates.includes(candidate)
    )
  })

  return canonicalizeCountryDictionaryRow(matchedItem)
}
